import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Users, Clock, Star } from 'lucide-react';
import Card from '../ui/Card.jsx';
import MiniStat from '../ui/MiniStat.jsx';
import AnimatedNumber from '../ui/AnimatedNumber.jsx';
import AuraIcon from '../ui/AuraIcon.jsx';
import JournalEntryRow, { FRESH_COUNT_DELAY_MS } from './JournalEntryRow';
import { formatDurationShort } from './journalUtils';

// Erst laden, wenn jemand auf eine Lernpartnerin tippt: Die geteilte Ansicht
// (ohne Login) braucht die Profilkarte nie.
const ProfileSheet = lazy(() => import('../profile/ProfileSheet.jsx'));

const readJson = (key) => {
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeJson = (key, value) => {
  if (!key) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Privatmodus o. Ae.: dann gibt es eben keinen „neu“-Moment.
  }
};

/**
 * Welche Eintraege kamen seit dem letzten Besuch dazu? Gemerkt werden die IDs
 * dieses Geraets. Beim allerersten Oeffnen gibt es keine Vergleichsbasis — dann
 * ist nichts „neu“ (sonst wuerde das ganze Logbuch auf einmal aufleuchten).
 */
const useFreshIds = (storageKey, ids) => {
  const [baseline] = useState(() => {
    const stored = readJson(storageKey);
    return Array.isArray(stored) ? new Set(stored) : null;
  });
  const fresh = useMemo(
    () => (baseline ? new Set(ids.filter(id => !baseline.has(id))) : new Set()),
    [baseline, ids],
  );
  useEffect(() => {
    if (ids.length > 0) writeJson(storageKey, ids.slice(0, 600));
  }, [storageKey, ids]);
  return fresh;
};

/** Zuletzt gesehene Aura-Summe, damit die Kennzahl vom alten Stand hochzaehlt. */
const useLastSeen = (storageKey, value, ready) => {
  const [from] = useState(() => {
    const stored = readJson(storageKey);
    return typeof stored === 'number' ? stored : undefined;
  });
  useEffect(() => {
    if (ready) writeJson(storageKey, value);
  }, [storageKey, value, ready]);
  return from;
};

/**
 * Das Lernlogbuch als EINE Karte (Okt 2026, „App wie Spot“, Motion-Kit
 * `ui.journalPage`): vier kompakte Kennzahlen als Kacheln mit Icon, wie auf
 * der Landingpage (die Kennzahl-Zeile aus ui/StatRow war hier kurz im Einsatz,
 * Heiko wollte die Kacheln zurueck, 21.10.2026), darunter die Eintraege als
 * ruhige Zeilen mit Haarlinie. Vorher: Kachelreihe + je Eintrag eine Karte.
 *
 * Lernpartner:innen fuehren aus der aufgeklappten Zeile weiter — in den Chat,
 * wenn ihr vernetzt seid, sonst zur Profilkarte (als Blatt, die Seite bleibt).
 */
const JournalCard = ({
  timeline,
  stats,
  userId,
  readOnly = false,
  showStats = true,
  onEditEntry = () => {},
  onDeleteEntry = () => {},
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(() => new Set());
  const [profileFor, setProfileFor] = useState(null);

  const ids = useMemo(() => timeline.map(item => `${item.type}:${item.data.id}`), [timeline]);
  const fresh = useFreshIds(!readOnly && userId ? `eo-journal-seen-${userId}` : null, ids);
  const auraFrom = useLastSeen(
    !readOnly && userId ? `eo-journal-aura-${userId}` : null,
    stats?.totalAura ?? 0,
    timeline.length > 0,
  );

  const toggle = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const openPartner = (partner) => {
    if (partner.connection?.status === 'connected' && partner.connection.chatId) {
      navigate(`/chat/${partner.connection.chatId}`);
      return;
    }
    setProfileFor(partner);
  };

  // Neue Sessions seit dem letzten Besuch: Die Kennzahl zaehlt von davor hoch.
  const freshSessions = useMemo(() => [...fresh].filter(id => id.startsWith('session:')).length, [fresh]);

  const ratingText = stats?.averageRating != null
    ? Number(stats.averageRating).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    : '–';

  return (
    <>
      <Card className="px-3.5 pb-1 pt-3.5 sm:px-[18px] sm:pb-1.5 sm:pt-[18px]">
        {showStats && stats && (
          <div className="mb-2 grid grid-cols-4 gap-2">
            <MiniStat
              icon={Users}
              value={freshSessions > 0
                ? <AnimatedNumber value={stats.sessionCount} from={stats.sessionCount - freshSessions} delay={FRESH_COUNT_DELAY_MS} />
                : stats.sessionCount}
              label="Sessions"
            />
            <MiniStat icon={Clock} value={formatDurationShort(stats.totalLearningTime)} label="Lernzeit" />
            <MiniStat icon={Star} value={ratingText} label="Ø Bewertung" />
            <MiniStat
              icon={AuraIcon}
              accent="aura"
              value={<AnimatedNumber value={stats.totalAura} from={auraFrom} duration={900} delay={fresh.size > 0 ? FRESH_COUNT_DELAY_MS : 0} />}
              label="Aura verdient"
            />
          </div>
        )}
        <ul aria-label="Einträge">
          {timeline.map((item, index) => {
            const key = ids[index];
            return (
              <JournalEntryRow
                key={key}
                item={item}
                fresh={fresh.has(key)}
                expanded={expanded.has(key)}
                onToggle={() => toggle(key)}
                readOnly={readOnly}
                onEdit={() => onEditEntry(item.data)}
                onDelete={() => onDeleteEntry(item.data.id)}
                onOpenPartner={openPartner}
              />
            );
          })}
        </ul>
      </Card>

      {!readOnly && profileFor && (
        <Suspense fallback={null}>
          <ProfileSheet
            open
            onClose={() => setProfileFor(null)}
            userId={profileFor.id}
            name={profileFor.displayName}
          />
        </Suspense>
      )}
    </>
  );
};

JournalCard.propTypes = {
  timeline: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.oneOf(['session', 'entry']).isRequired,
    data: PropTypes.object.isRequired,
  })).isRequired,
  stats: PropTypes.shape({
    sessionCount: PropTypes.number,
    totalLearningTime: PropTypes.number,
    averageRating: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    totalAura: PropTypes.number,
  }),
  userId: PropTypes.string,
  readOnly: PropTypes.bool,
  showStats: PropTypes.bool,
  onEditEntry: PropTypes.func,
  onDeleteEntry: PropTypes.func,
};

export default JournalCard;
